import toast from "react-hot-toast";
import { trackPromise } from "react-promise-tracker";

import {
  disableTeamMember,
  enableTeamMember,
  getTeam,
  resendTeamMemberInvite,
  updateTeamMemberRole,
} from "./automatic";

/**
 * Disables a team member
 * @param {Object} member - The team member to disable
 * @param {string} clientId - Current client ID
 * @param {Function} onCloseModal - Callback to close the modal
 * @param {Function} setTeam - State setter to update team list
 */
export const disableUser = async (
  member: Record<string, any>,
  clientId: string,
  onCloseModal: () => void,
  setTeam: (team: Record<string, any>[]) => void
) => {
  if (!member) return;

  const [disableUserResponse, disableUserError] = await disableTeamMember(
    member,
    clientId
  );
  if (disableUserError) {
    toast.error(disableUserError.message || "Failed to disable user");
    console.error(disableUserError);
    return;
  }
  toast.success("User disabled successfully");

  if (onCloseModal) {
    onCloseModal();
  }

  // Refresh team list
  if (clientId && setTeam) {
    trackPromise(
      getTeam(clientId).then((data: Record<string, any>[]) => {
        setTeam(data);
      })
    );
  }
};

/**
 * Enables a team member
 * @param {Object} member - The team member to enable
 * @param {string} clientId - Current client ID
 * @param {Function} onCloseModal - Callback to close the modal
 * @param {Function} setTeam - State setter to update team list
 */
export const enableUser = async (
  member: Record<string, any>,
  clientId: string,
  onCloseModal: () => void,
  setTeam: (team: Record<string, any>[]) => void
) => {
  if (!member) return;

  const [enableUserResponse, enableUserError] = await enableTeamMember(
    member,
    clientId
  );
  if (enableUserError) {
    toast.error(enableUserError.message || "Failed to enable user");
    console.error(enableUserError);
    return;
  }
  toast.success("User enabled successfully");

  if (onCloseModal) {
    onCloseModal();
  }

  // Refresh team list
  if (clientId && setTeam) {
    trackPromise(
      getTeam(clientId).then((data: Record<string, any>[]) => {
        setTeam(data);
      })
    );
  }
};

/**
 * Updates a team member's permissions/role
 * @param {Object} member - The team member to update
 * @param {string} oldRole - Current role
 * @param {string} newRole - New role to assign
 * @param {string} clientId - Current client ID
 * @param {Function} onCloseModal - Callback to close the modal
 * @param {Function} setTeam - State setter to update team list
 */
export const updatePermissions = async (
  member: Record<string, any>,
  oldRole: string,
  newRole: string,
  clientId: string,
  onCloseModal: () => void,
  setTeam: (team: Record<string, any>[]) => void
) => {
  if (!member) return;

  const [updateRoleResponse, updateRoleError] = await updateTeamMemberRole(
    member,
    oldRole,
    newRole,
    clientId
  );
  if (updateRoleError) {
    toast.error(updateRoleError.message || "Failed to update user permissions");
    console.error(updateRoleError);
    return;
  }

  const roleLabel = newRole
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  toast.success(`User role updated to ${roleLabel}`);

  if (onCloseModal) {
    onCloseModal();
  }

  // Refresh team list
  if (clientId && setTeam) {
    trackPromise(
      getTeam(clientId).then((data: Record<string, any>[]) => {
        setTeam(data);
      })
    );
  }
};

/**
 * Resends an invite to a team member
 * @param {Object} member - The team member to resend invite to
 * @param {string} clientId - Current client ID
 */
export const resendInvite = async (
  member: Record<string, any>,
  clientId: string
) => {
  const [resendInviteResponse, resendInviteError] =
    await resendTeamMemberInvite(member, clientId);
  if (resendInviteError) {
    if (
      resendInviteError.message ===
      "User is not in the correct state to resend an invite."
    ) {
      toast.error("User needs to be in a pending state to resend an invite.");
    } else {
      toast.error(resendInviteError.message || "Failed to resend invite");
    }
  } else {
    toast.success("Invite resent successfully");
  }
};
